// ── time, the way a person says it ──────────────────────────────────────────
// netti never shows machine time. This is the one place an ISO stamp becomes
// "yesterday" or "three weeks ago", and the one place "last week" becomes a
// range the activity tools can query. Pure, so it is testable to the day.

const DAY = 86_400_000;
const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Days are reckoned in UTC on both sides — the stamps being compared are UTC
// instants from the database, and a server in one zone must not shift a
// person's "yesterday" by a day.
const startOfDay = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
const utcDate = (y: number, m: number, day: number) => new Date(Date.UTC(y, m, day));

/** "today" · "yesterday" · "on tuesday" · "three weeks ago" · "back in june" · "last year". */
export function humanTime(iso: string | Date | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.round((startOfDay(now).getTime() - startOfDay(d).getTime()) / DAY);
  if (days < 0) return days === -1 ? 'tomorrow' : 'soon';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `on ${DAYS[d.getUTCDay()]}`;
  if (days < 14) return 'last week';
  if (days < 21) return 'two weeks ago';
  if (days < 28) return 'three weeks ago';
  if (days < 45) return 'about a month ago';
  if (d.getUTCFullYear() === now.getUTCFullYear()) return `back in ${MONTHS[d.getUTCMonth()]}`;
  if (now.getUTCFullYear() - d.getUTCFullYear() === 1) return `last ${MONTHS[d.getUTCMonth()]}`;
  return `in ${d.getUTCFullYear()}`;
}

/** Rewrite every ISO stamp in a text as human time. Leaves paths + links alone. */
export function humaniseTimes(text: string, now: Date = new Date()): string {
  return text
    .replace(/\b(\d{4}-\d{2}-\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?\b(?!\S*\))/g, (m, day: string) => humanTime(`${day}T12:00:00`, now) || m)
    .replace(/\s*\b(UTC|GMT[+-]?\d*)\b/g, '');
}

export interface WhenRange { from: string; to: string; label: string }

/** Read a "when" out of plain words into a [from, to) range. Null when there is none. */
export function parseWhen(text: string, now: Date = new Date()): WhenRange | null {
  const t = (text ?? '').toLowerCase().trim();
  if (!t) return null;
  const today = startOfDay(now);
  const range = (from: Date, days: number, label: string): WhenRange => ({
    from: from.toISOString(),
    to: new Date(from.getTime() + days * DAY).toISOString(),
    label,
  });
  const iso = t.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso) return range(utcDate(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])), 1, `on ${iso[0]}`);
  if (/\btoday\b/.test(t)) return range(today, 1, 'today');
  if (/\byesterday\b/.test(t)) return range(new Date(today.getTime() - DAY), 1, 'yesterday');
  if (/\bthis week\b/.test(t)) {
    const from = new Date(today.getTime() - ((today.getUTCDay() + 6) % 7) * DAY);
    return range(from, 7, 'this week');
  }
  if (/\blast week\b|\bpast week\b|\b(last|past) 7 days\b/.test(t)) return range(new Date(today.getTime() - 7 * DAY), 8, 'the last week');
  if (/\b(last|past) (two|2) weeks\b/.test(t)) return range(new Date(today.getTime() - 14 * DAY), 15, 'the last two weeks');
  if (/\bthis month\b/.test(t)) return range(utcDate(today.getUTCFullYear(), today.getUTCMonth(), 1), 32, 'this month');
  if (/\blast month\b|\bpast month\b|\b(last|past) 30 days\b/.test(t)) return range(new Date(today.getTime() - 30 * DAY), 31, 'the last month');
  const ago = t.match(/\b(\d+|a|an|one|two|three|four|five|six|seven|ten)\s+(day|week|month)s?\s+ago\b/);
  if (ago) {
    const words: Record<string, number> = { a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, ten: 10 };
    const n = words[ago[1]] ?? Number(ago[1]);
    const unit = ago[2] === 'day' ? 1 : ago[2] === 'week' ? 7 : 30;
    const from = new Date(today.getTime() - n * unit * DAY);
    return range(from, unit === 1 ? 1 : unit, `${ago[1]} ${ago[2]}${n === 1 ? '' : 's'} ago`);
  }
  for (let i = 0; i < DAYS.length; i++) {
    if (new RegExp(`\\b(?:last |on )?${DAYS[i]}\\b`).test(t)) {
      let back = (today.getUTCDay() - i + 7) % 7;
      if (back === 0) back = 7;
      return range(new Date(today.getTime() - back * DAY), 1, `on ${DAYS[i]}`);
    }
  }
  for (let i = 0; i < MONTHS.length; i++) {
    if (new RegExp(`\\b${MONTHS[i]}\\b|\\b${MONTHS[i].slice(0, 3)}\\b`).test(t)) {
      const year = i > now.getUTCMonth() ? now.getUTCFullYear() - 1 : now.getUTCFullYear();
      const from = utcDate(year, i, 1);
      return range(from, 32, `in ${MONTHS[i]}`);
    }
  }
  return null;
}
